import {
  LayoutDashboard,
  Sparkles,
  Users,
  Calendar,
  StickyNote,
  CheckSquare,
  Handshake,
  Briefcase,
  Database,
  DollarSign,
  PieChart,
  Package,
  Settings,
  Building,
  Building2,
  Home,
  UserCog,
  Palette,
  Plug,
  Shield,
  ChevronDown,
  TrendingUp,
  Mail,
  FileCheck,
  ShieldCheck,
  Bot,
  Wallet,
  Network,
  FileText,
  GitBranch,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTenant } from "@/hooks/useTenant";
import { useBranding } from "@/hooks/useBranding";
import { useState } from "react";

const crmItems = [
  { title: "Contacts", url: "/crm/contacts", icon: Users },
  { title: "Notes & Meetings", url: "/crm/meetings", icon: Calendar },
  { title: "Actions", url: "/crm/actions", icon: CheckSquare },
  { title: "Deals", url: "/crm/deals", icon: GitBranch },
];

const copilotItems = [
  { title: 'Copilot', url: '/copilot', icon: Bot },
  // Consolidated from CIO, Compliance, Portfolio assistants
];

const agenticItems = [
  { title: "Document Chaser", url: "/agentic/chasers", icon: Mail },
  { title: "Workflows", url: "/agentic/workflows", icon: Sparkles },
  { title: "Portfolio Agents", url: "/agentic/portfolio-agents", icon: TrendingUp },
  { title: "Referrals", url: "/agentic/referrals", icon: Handshake },
  { title: "Tax Tracker", url: "/agentic/tax-tracker", icon: FileCheck },
  { title: "Meeting Prep", url: "/agentic/meeting-prep", icon: Calendar },
  { title: "Cash Flow", url: "/agentic/cash-flow", icon: Wallet },
  { title: "Fee Audit", url: "/agentic/fee-audit", icon: DollarSign },
  { title: "Commitment Pacing", url: "/agentic/commitment-pacing", icon: PieChart },
  { title: "Reporting", url: "/agentic/reporting", icon: FileCheck },
];

const portfolioItems = [
  { title: "Overview", url: "/portfolio/positions", icon: Database, badge: "Unified" },
  { title: "Alternatives", url: "/portfolio/alternatives", icon: Package },
  { title: "Analytics", url: "/portfolio/analytics", icon: TrendingUp },
  { title: "Transactions", url: "/portfolio/transactions", icon: Briefcase },
  { title: "Valuations", url: "/portfolio/valuations", icon: TrendingUp },
  { title: "Reports", url: "/portfolio/reports", icon: FileText },
];

const dataItems = [
  { title: "Ingestion", url: "/data/ingestion", icon: Database },
  // Document Sources hidden for MVP — route still accessible via direct URL
];

const settingsItems = [
  { title: "Organization", url: "/settings/organization", icon: Building },
  { title: "Users", url: "/settings/users", icon: UserCog },
  { title: "Entities", url: "/settings/entities", icon: Building },
  { title: "Fund Catalog", url: "/settings/funds", icon: Briefcase },
  { title: "Security", url: "/settings/security", icon: ShieldCheck },
  { title: "Audit Logs", url: "/settings/audit-logs", icon: Shield },
  { title: "Branding", url: "/settings/branding", icon: Palette },
];

// MFO Firm-level items (only visible for multi-family offices)
const firmItems = [
  { title: "Firm Dashboard", url: "/firm", icon: Building2 },
  { title: "All Families", url: "/firm/families", icon: Users },
  { title: "GP Exposure", url: "/firm/gp-exposure", icon: Network },
  { title: "Firm Copilot", url: "/firm/copilot", icon: Bot },
  { title: "Firm Agents", url: "/firm/agents", icon: Sparkles },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { currentOrg, isSFO, isMFO, isClientView } = useTenant();
  const { logoUrl } = useBranding();
  const location = useLocation();
  
  const [firmOpen, setFirmOpen] = useState(location.pathname.startsWith('/firm'));
  const [crmOpen, setCrmOpen] = useState(location.pathname.startsWith('/crm'));
  const [portfolioOpen, setPortfolioOpen] = useState(location.pathname.startsWith('/portfolio'));
  const [dataOpen, setDataOpen] = useState(location.pathname.startsWith('/data'));
  const [copilotOpen, setCopilotOpen] = useState(location.pathname.startsWith('/copilot'));
  const [agenticOpen, setAgenticOpen] = useState(location.pathname.startsWith('/agentic'));
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/settings'));

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="h-14 border-b border-sidebar-border px-4 flex items-center">
        <div className="flex items-center gap-3 min-w-0">
          <img src={logoUrl} alt={currentOrg?.name || 'Vintage'} className="h-8 w-8 shrink-0 rounded-lg shadow-md object-contain" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <h2 className="text-sidebar-foreground font-semibold text-sm leading-tight truncate" title={currentOrg?.name || 'Vintage'}>
                {currentOrg?.name || 'Vintage'}
              </h2>
              <p className="text-sidebar-foreground/60 text-xs leading-tight mt-0.5">
                {isSFO ? 'Single Family Office' : isMFO ? 'Multi Family Office' : 'Family Office SoR'}
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Dashboard */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="KPIs and what needs attention">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                    }
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    {!collapsed && <span>Dashboard</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarSeparator className="my-1" />

              {/* Firm Overview - MFO Only, hidden in client view */}
              {isMFO && !isClientView && (
                <Collapsible open={firmOpen} onOpenChange={setFirmOpen} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Firm-wide overview across all families">
                        <Building2 className="h-4 w-4" />
                        {!collapsed && <span>Firm Overview</span>}
                        {!collapsed && (
                          <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        )}
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {firmItems.map((item) => (
                          <SidebarMenuSubItem key={item.title}>
                            <SidebarMenuSubButton asChild>
                              <NavLink
                                to={item.url}
                                className={({ isActive }) =>
                                  isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                                }
                              >
                                <item.icon className="h-4 w-4" />
                                <span>{item.title}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}

              {isMFO && !isClientView && <SidebarSeparator className="my-1" />}

              {/* CRM */}
              <Collapsible open={crmOpen} onOpenChange={setCrmOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Relationships Management">
                      <Users className="h-4 w-4" />
                      {!collapsed && <span>Relationships</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {crmItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={item.url}
                              className={({ isActive }) => 
                                isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <SidebarSeparator className="my-1" />

              {/* Portfolio */}
              <Collapsible open={portfolioOpen} onOpenChange={setPortfolioOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Portfolio Management">
                      <Briefcase className="h-4 w-4" />
                      {!collapsed && <span>Portfolio</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {portfolioItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={item.url}
                              className={({ isActive }) => 
                                isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                              {item.badge && (
                                <span className="ml-auto text-xs text-muted-foreground">{item.badge}</span>
                              )}
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <SidebarSeparator className="my-1" />

              {/* Data */}
              <Collapsible open={dataOpen} onOpenChange={setDataOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Data Management">
                      <Database className="h-4 w-4" />
                      {!collapsed && <span>Data</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {dataItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={item.url}
                              className={({ isActive }) => 
                                isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <SidebarSeparator className="my-1" />

              {/* Copilot */}
              <Collapsible open={copilotOpen} onOpenChange={setCopilotOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="AI Analysis Assistants">
                      <Bot className="h-4 w-4" />
                      {!collapsed && <span>Copilot</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {copilotItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={item.url}
                              className={({ isActive }) => 
                                isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <SidebarSeparator className="my-1" />

              {/* Automation */}
              <Collapsible open={agenticOpen} onOpenChange={setAgenticOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="AI-powered automation">
                      <Sparkles className="h-4 w-4" />
                      {!collapsed && <span>Automation</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {agenticItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={item.url}
                              className={({ isActive }) => 
                                isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              <SidebarSeparator className="my-1" />

              {/* Settings */}
              <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Application Settings">
                      <Settings className="h-4 w-4" />
                      {!collapsed && <span>Settings</span>}
                      {!collapsed && (
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {settingsItems
                        .filter((item) => !item.mfoOnly || isMFO)
                        .map((item) => (
                          <SidebarMenuSubItem key={item.title}>
                            <SidebarMenuSubButton asChild>
                              <NavLink 
                                to={item.url}
                                className={({ isActive }) => 
                                  isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
                                }
                              >
                                <item.icon className="h-4 w-4" />
                                <span>{item.title}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
